import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SimulationForm, { responseToForm } from './SimulationForm';
import SimulationResults from './SimulationResults';
import { getSimulation } from '../../api/simulation';
import { useAuth } from '../../auth/AuthContext';

// ── Simulation card in list view ──────────────────────────────────────────────
function SimulationCard({ sim, parentName, onView, onFork }) {
  const date = new Date(sim.createdAt).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  return (
    <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4 transition hover:bg-white/[0.07]">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-base font-bold text-white">{sim.name}</span>
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold
            ${sim.handlingType === 'palletized'
              ? 'bg-violet-500/20 text-violet-300'
              : 'bg-cyan-500/20 text-cyan-300'}`}>
            {sim.handlingType === 'palletized' ? 'Palletized' : 'Hand Stuffed'}
          </span>
        </div>
        {parentName && (
          <p className="mt-0.5 text-xs text-slate-500">↳ forked from {parentName}</p>
        )}
        <p className="mt-1 text-xs text-slate-500">
          {date}
          {' · '}{sim.containerCount} container type{sim.containerCount !== 1 ? 's' : ''}
          {' · '}{(sim.totalPackages || 0).toLocaleString()} packages
        </p>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={onView}
          className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-white/10 hover:text-white transition"
        >
          View Results
        </button>
        <button
          onClick={onFork}
          title="Fork this simulation"
          className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-white/10 hover:text-white transition"
        >
          Fork
        </button>
      </div>
    </div>
  );
}

// ── Main SimulationsTab ───────────────────────────────────────────────────────
// view: 'list' | 'create' | 'result'
export function SimulationsTab({ historyApi, sharedSimId, assetHook }) {
  const { sims, addSimulation } = historyApi;
  const { user } = useAuth();
  const navigate = useNavigate();

  // Beyond CBM's "Run in console" hands off the logged-in user's picks via
  // sessionStorage (same mechanism as CBM Calculator's — unused — cbm_prefill
  // attempt) rather than route state, so a page refresh mid-handoff doesn't
  // lose it. The initializer only READS — no side effect — because React 18
  // StrictMode double-invokes useState initializers in dev; a removeItem()
  // in there meant the first invocation consumed the data and the discarded
  // second invocation (whose result React actually keeps) found nothing.
  // Clearing happens once, separately, in the effect below.
  const [prefillData] = useState(() => {
    try {
      const raw = sessionStorage.getItem('beyond_cbm_prefill');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  useEffect(() => {
    if (prefillData) sessionStorage.removeItem('beyond_cbm_prefill');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [view,             setView]             = useState(sharedSimId ? 'result' : (prefillData ? 'create' : 'list'));
  const [forkSource,       setForkSource]       = useState(null);
  const [forkInitialData,  setForkInitialData]  = useState(prefillData);
  const [activeSim,        setActiveSim]        = useState(null);
  const [activeResult,     setActiveResult]     = useState(null);
  const [resultError,      setResultError]      = useState(false);

  // Load simulation from URL ?id= param (shared links + View Results navigation)
  useEffect(() => {
    if (!sharedSimId) return;
    setResultError(false);
    getSimulation(sharedSimId, user?.token, user?.id)
      .then((data) => {
        setActiveResult(data);
        const sim = sims.find(s => s.simulationId === sharedSimId);
        if (sim) setActiveSim(sim);
        setView('result');
      })
      .catch(() => setResultError(true));
  }, [sharedSimId, sims, user]);

  const goList   = () => { setView('list'); setForkSource(null); setForkInitialData(null); setActiveResult(null); setActiveSim(null); setResultError(false); navigate('/console', { replace: true }); };
  const goCreate = () => { setForkSource(null); setForkInitialData(null); setView('create'); };
  const goFork   = async (sim) => {
    setForkSource(sim);
    try {
      const data = await getSimulation(sim.simulationId, user?.token, user?.id);
      setForkInitialData(responseToForm(data));
    } catch {
      setForkInitialData(null);
    }
    setView('create');
  };

  const handleViewResult = (sim) => {
    navigate(`/console?id=${sim.simulationId}`);
  };

  const handleResults = async (data, form) => {
    const simNumber = sims.length + 1;
    const record = {
      simulationId:   data.id,
      name:           `Simulation #${simNumber}`,
      parentId:       forkSource?._id  || forkSource?.id  || null,
      handlingType:   form.palletization ? 'palletized' : 'hand_stuffed',
      totalPackages:  form.packages.reduce((s, p) => s + (p.quantity || 0), 0),
      containerCount: form.containers.length,
      // formData removed — input lives in simulation_results, fetched via simulationId on fork.
    };
    try {
      const saved = await addSimulation(record);
      setActiveSim(saved || record);
      setActiveResult(data);
      setForkSource(null);
      setView('result');
    } catch (err) {
      // Limit reached on server — stay on create view, show error
      alert(err.message || 'Could not save simulation');
    }
  };

  // ── Create / fork view ──────────────────────────────────────────────────────
  if (view === 'create') {
    return (
      <div className="p-8">
        <button
          onClick={goList}
          className="mb-6 flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition"
        >
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          Back to simulations
        </button>

        <div className="mb-6">
          <h1 className="text-2xl font-black text-white">
            {forkSource ? `Fork of ${forkSource.name}` : 'New Simulation'}
          </h1>
          {forkSource && (
            <p className="mt-1 text-sm text-slate-400">
              Pre-filled from <span className="text-white font-semibold">{forkSource.name}</span>.
              Tweak any values and run to create a variant.
            </p>
          )}
        </div>

        <SimulationForm
          key={forkSource?.id || 'new'}
          initialData={forkInitialData}
          onResults={handleResults}
          assetHook={assetHook}
        />
      </div>
    );
  }

  // ── Result view ─────────────────────────────────────────────────────────────
  if (view === 'result') {
    return (
      <div className="p-8">
        <div className="mb-6 flex items-center justify-between gap-4">
          <button
            onClick={goList}
            className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition"
          >
            <span className="material-symbols-outlined text-[18px]">arrow_back</span>
            Back to simulations
          </button>
          {activeSim && (
            <button
              onClick={() => goFork(activeSim)}
              className="flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-slate-300 hover:bg-white/10 hover:text-white transition"
            >
              <span className="material-symbols-outlined text-[16px]">edit</span>
              Edit &amp; re-run
            </button>
          )}
        </div>
        {!activeResult && !resultError && (
          <div className="flex items-center justify-center py-24 text-slate-500 text-sm">
            <span className="material-symbols-outlined animate-spin mr-2 text-[18px]">progress_activity</span>
            Loading results…
          </div>
        )}
        {resultError && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <span className="material-symbols-outlined text-5xl text-slate-600">error</span>
            <p className="mt-4 text-lg font-bold text-slate-300">Simulation not found</p>
            <p className="mt-1 text-sm text-slate-500">This link may be invalid or the simulation was deleted.</p>
            <button
              onClick={goList}
              className="mt-6 rounded-xl bg-white/5 border border-white/10 px-5 py-2.5 text-sm font-semibold text-slate-300 hover:bg-white/10 hover:text-white transition"
            >
              Back to simulations
            </button>
          </div>
        )}
        {activeResult && <SimulationResults
          results={activeResult}
        />}
      </div>
    );
  }

  // ── List view ───────────────────────────────────────────────────────────────
  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Console</p>
          <h1 className="mt-1 text-3xl font-black text-white">Simulations</h1>
          {/* Saving/history is unlimited — only running an "expanded" sim
              (more than 2 containers/items, palletized, or custom
              strategies) spends a one-time credit, enforced server-side. */}
          <p className="mt-1 text-sm text-slate-400">
            {user?.plan === 'paid'
              ? 'Unlimited simulations'
              : `${user?.freeSimCreditsUsed ?? 0} / ${user?.freeSimCreditsLimit ?? 10} free expanded simulations used · up to 2 containers/2 items always free`}
          </p>
        </div>

        <button
          onClick={goCreate}
          className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-blue-500 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-600/20 transition hover:brightness-110 active:scale-95"
        >
          <span className="material-symbols-outlined text-[18px]">add</span>
          New Simulation
        </button>
      </div>

      {/* Empty state */}
      {sims.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03] py-24 text-center">
          <span className="material-symbols-outlined text-5xl text-slate-600">inventory_2</span>
          <p className="mt-4 text-lg font-bold text-slate-300">No simulations yet</p>
          <p className="mt-1 text-sm text-slate-500">Run your first simulation to see it here.</p>
          <button
            onClick={goCreate}
            className="mt-6 flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-blue-500 px-5 py-2.5 text-sm font-bold text-white transition hover:brightness-110"
          >
            <span className="material-symbols-outlined text-[18px]">add</span>
            New Simulation
          </button>
        </div>
      )}

      {/* Simulation list */}
      {sims.length > 0 && (
        <div className="space-y-3">
          {sims.map((sim) => (
            <SimulationCard
              key={sim._id || sim.id}
              sim={sim}
              parentName={sims.find(s => (s._id || s.id) === sim.parentId)?.name}
              onView={() => handleViewResult(sim)}
              onFork={() => goFork(sim)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
